import { useCallback, useMemo } from 'react'
import { styled, Typography, TypographyProps } from '@mui/material'
import {
  AddUserIcon,
  ChannelIcon,
  GroupIcon,
  ModeIcon,
  ShareIcon,
} from '@weblite/icons'
import { useOpenedRoomId } from '$logic/opened-room'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { useHall } from '$logic/hall/react-hook'
import { useRoom } from '$logic/room'

import { PrimaryOutlinedButton } from '$common/buttons/primary-outlined-button'
import { t } from '$i18n'
import {
  openAddHallMembers,
  openHallInviteLinks,
  openHallSettings,
} from '$logic/router'
import { Separator } from './shared/separator'

interface Props {
  withSeparator?: boolean
}
export function HallOnboarding({ withSeparator }: Props) {
  const roomId = useOpenedRoomId()
  const { entity: hall } = useHall(roomId)
  const room = useRoom(roomId)

  const hallName = hall?.name
  const isGroup = room?.type === 'GROUP'
  const permissions = useClientUserPermissions(roomId)
  const canAddMembers = permissions?.addMembers
  const canChangeHallInfo = permissions?.changeHallInfo
  const translationKey = `message.onboarding.${isGroup ? 'group' : 'channel'}`

  const actions = useMemo(() => {
    const arr: string[] = []
    if (canAddMembers) arr.push(t(`${translationKey}.actions.addMember`))
    if (canChangeHallInfo)
      arr.push(t(`${translationKey}.actions.addDescription`))
    arr.push(t(`${translationKey}.actions.shareInviteLink`))
    return arr
  }, [canAddMembers, canChangeHallInfo])

  const handleAddMembers = useCallback(() => {
    if (!room) return
    openAddHallMembers(room.id, true)
  }, [room])

  const handleChangeHallInfo = useCallback(() => {
    if (!room) return
    openHallSettings(room.id)
  }, [room])

  const handleShareInviteLink = useCallback(() => {
    if (!room) return
    openHallInviteLinks(room.id)
  }, [room])

  return (
    <>
      <Root sx={{ px: '20px', pt: withSeparator ? '20px' : 0 }}>
        <Container>
          <DetailsContainer>
            <IconWrapper>
              {isGroup ? (
                <GroupIcon sx={{ width: '40px', height: '40px' }} />
              ) : (
                <ChannelIcon sx={{ width: '40px', height: '40px' }} />
              )}
            </IconWrapper>
            <DescriptionContainer>
              <Typography
                variant="subtitle1"
                color="neutral900"
                textAlign="center"
              >
                {t(`${translationKey}.title`)(hallName || '')}
              </Typography>
              <Typography
                textAlign="center"
                variant="paragraph3"
                color="neutral800"
              >
                {t(`${translationKey}.description`)(actions)}
              </Typography>
            </DescriptionContainer>
          </DetailsContainer>

          <ActionButtons>
            {canAddMembers && (
              <PrimaryOutlinedButton
                fullWidth
                startIcon={<AddUserIcon width="20px" height="20px" />}
                text={
                  <ActionButtonText>
                    {t(`${translationKey}.actionButtons.addMember`)}
                  </ActionButtonText>
                }
                onClick={handleAddMembers}
              />
            )}
            {canChangeHallInfo && (
              <PrimaryOutlinedButton
                fullWidth
                text={
                  <ActionButtonText>
                    {t(`${translationKey}.actionButtons.addDescription`)}
                  </ActionButtonText>
                }
                onClick={handleChangeHallInfo}
                startIcon={<ModeIcon width="20px" height="20px" />}
              />
            )}
            <PrimaryOutlinedButton
              fullWidth
              text={
                <ActionButtonText>
                  {t(`${translationKey}.actionButtons.shareInviteLink`)}
                </ActionButtonText>
              }
              onClick={handleShareInviteLink}
              startIcon={<ShareIcon width="20px" height="20px" />}
            />
          </ActionButtons>
        </Container>
      </Root>
      {withSeparator && <Separator />}
    </>
  )
}

const Root = styled('div')`
  background-color: transparent;
  display: flex;
  justify-content: center;
`

const Container = styled('div')`
  background-color: ${({ theme }) => theme.palette.neutral000};
  box-shadow: ${({ theme }) => theme.shadows[3]};
  border-radius: 20px;
  padding: 20px;
  gap: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
`

const DetailsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`

const IconWrapper = styled('div')`
  width: 64px;
  height: 64px;
  background-color: ${({ theme }) => theme.palette.primary100};
  color: ${({ theme }) => theme.palette.primary500};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const DescriptionContainer = styled('div')`
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: center;
`

const ActionButtons = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const ActionButtonText = (props: TypographyProps) => (
  <Typography {...props} variant="subtitle3" color="inherit" />
)
